
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const features = [
  { icon: "🥫", title: "Smart Pantry", desc: "Track what you have at home with your digital fridge.", path: "/pantry" },
  { icon: "📖", title: "Recipes", desc: "Discover dishes and get step-by-step cooking guides.", path: "/recipes" },
  { icon: "🛒", title: "Grocery List", desc: "Auto-generate shopping lists based on your pantry.", path: "/grocery" },
  { icon: "📍", title: "Nearby Stores", desc: "Find the closest markets and grocery shops.", path: "/stores" },
  { icon: "🌍", title: "Community", desc: "Share your dishes and get feedback from other cooks.", path: "/community" },
];

export default function Home() {
  const navigate = useNavigate();
  return (
    <div style={styles.page}>
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <div style={styles.heroBadge}>✨ Your cozy kitchen companion</div>
          <h1 style={styles.heroTitle}>
            Welcome to<br />
            <span style={styles.heroAccent}>CaféPantry</span>
          </h1>
          <p style={styles.heroDesc}>
            Manage your pantry, discover beautiful recipes, generate smart grocery lists,
            and share your creations with a warm community of home cooks.
          </p>
          <div style={styles.heroActions}>
            <Link to="/pantry" className="btn-primary" style={styles.heroCta}>
              Open my Pantry 🥫
            </Link>
            <Link to="/recipes" className="btn-secondary" style={styles.heroCtaSecondary}>
              Browse Recipes →
            </Link>
          </div>
        </div>
        <div style={styles.heroIllustration}>
          <div style={styles.bigEmoji}>🍳</div>
          <div style={styles.floatingBadge1}>🥚 6 eggs</div>
          <div style={styles.floatingBadge2}>🍅 4 tomatoes</div>
          <div style={styles.floatingBadge3}>📖 4 recipes</div>
          <div style={styles.floatingBadge4}>🌶️ 2 tbsp chilli powder</div>
          <div style={styles.floatingBadge5}>🍋 1 lemon</div>
          <div style={styles.floatingBadge6}>🥑 2 avocado</div>
        </div>
      </section>

      {/* Flow banner */}
      <section style={styles.flow}>
        {["Check Pantry 🥫", "→", "Pick a Dish 🍽️", "→", "Buy Groceries 🛒", "→", "Cook & Share 🌍"].map((s, i) => (
          <span key={i} style={s === "→" ? styles.arrow : styles.flowStep}>{s}</span>
        ))}
      </section>

      {/* Feature cards */}
      <section>
        <h2 style={styles.sectionTitle}>Everything you need in one cozy spot</h2>
        <div style={styles.grid}>
          {features.map((f) => (
            <Link to={f.path} key={f.path} style={{ textDecoration: "none" }}>
              <div className="card" style={styles.featureCard}>
                <div style={styles.featureIcon}>{f.icon}</div>
                <h3 style={styles.featureTitle}>{f.title}</h3>
                <p style={styles.featureDesc}>{f.desc}</p>
                <span style={styles.featureArrow}>Explore →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section style={styles.ctaStrip}>
        <h2 style={styles.ctaTitle}>Ready to cook something wonderful?</h2>
        <p style={styles.ctaDesc}>Start by adding ingredients you have at home.</p>
        <Link to="/pantry" className="btn-primary">
          Add to Pantry 🥫
        </Link>
      </section>
    </div>
  );
}

const styles = {
  page: {
    maxWidth: "1100px",
    margin: "0 auto",
    padding: "2rem 1.5rem 4rem",
  },
  hero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "2rem",
    padding: "3rem 0 2.5rem",
    flexWrap: "wrap",
  },
  heroContent: { flex: "1", minWidth: "280px" },
  heroBadge: {
    display: "inline-block",
    background: "#fae4cc",
    color: "#9b7b54",
    borderRadius: "50px",
    padding: "0.3rem 1rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    marginBottom: "1rem",
    letterSpacing: "0.04em",
  },
  heroTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "clamp(2.2rem, 5vw, 3.4rem)",
    lineHeight: 1.15,
    color: "#3d2b1a",
    marginBottom: "1rem",
  },
  heroAccent: { color: "#9b7b54" },
  heroDesc: {
    color: "#9b7b54",
    fontSize: "1.05rem",
    lineHeight: 1.7,
    maxWidth: "460px",
    marginBottom: "2rem",
  },
  heroActions: { display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" },
  heroCta: {
    background: "#9b7b54",
    color: "#fff9f2",
    border: "none",
    padding: "0.75rem 1.6rem",
    borderRadius: "50px",
    fontSize: "0.95rem",
    fontWeight: 700,
    textDecoration: "none",
    display: "inline-block",
  },
  heroCtaSecondary: {
    color: "#9b7b54",
    border: "1.5px solid #9b7b54",
    padding: "0.7rem 1.4rem",
    borderRadius: "50px",
    fontSize: "0.95rem",
    fontWeight: 700,
    textDecoration: "none",
    display: "inline-block",
  },
  heroIllustration: {
    flex: "1",
    minWidth: "220px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    height: "260px",
  },
  bigEmoji: {
    fontSize: "7rem",
    filter: "drop-shadow(0 8px 24px rgba(155,123,84,0.2))",
    animation: "float 3s ease-in-out infinite",
  },
  floatingBadge1: {
    position: "absolute",
    top: "20px",
    right: "10px",
    background: "#fff9f2",
    border: "1.5px solid #e0cdb0",
    borderRadius: "50px",
    padding: "0.3rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    color: "#6b4f2e",
    boxShadow: "0 4px 12px rgba(107,79,46,0.1)",
  },
  floatingBadge2: {
    position: "absolute",
    bottom: "40px",
    left: "40px",
    background: "#fff9f2",
    border: "1.5px solid #e0cdb0",
    borderRadius: "50px",
    padding: "0.3rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    color: "#6b4f2e",
    boxShadow: "0 4px 12px rgba(107,79,46,0.1)",
  },
  floatingBadge3: {
    position: "absolute",
    bottom: "10px",
    right: "80px",
    background: "#fff9f2",
    border: "1.5px solid #e0cdb0",
    borderRadius: "50px",
    padding: "0.3rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    color: "#6b4f2e",
    boxShadow: "0 4px 12px rgba(107,79,46,0.1)",
  },
  floatingBadge4: {
    position: "absolute",
    top: "5px",
    left: "0x",
    background: "#fff9f2",
    border: "1.5px solid #e0cdb0",
    borderRadius: "50px",
    padding: "0.3rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    color: "#6b4f2e",
    boxShadow: "0 4px 12px rgba(107,79,46,0.1)",
  },
  floatingBadge5: {
    position: "absolute",
    top: "90px",
    left: "40px",
    background: "#fff9f2",
    border: "1.5px solid #e0cdb0",
    borderRadius: "50px",
    padding: "0.3rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    color: "#6b4f2e",
    boxShadow: "0 4px 12px rgba(107,79,46,0.1)",
  },
  floatingBadge6: {
    position: "absolute",
    top: "120px",
    right: "20px",
    background: "#fff9f2",
    border: "1.5px solid #e0cdb0",
    borderRadius: "50px",
    padding: "0.3rem 0.9rem",
    fontSize: "0.82rem",
    fontWeight: 700,
    color: "#6b4f2e",
    boxShadow: "0 4px 12px rgba(107,79,46,0.1)",
  },
  flow: {
    background: "#f0e8d8",
    borderRadius: "16px",
    padding: "1rem 1.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    flexWrap: "wrap",
    marginBottom: "3rem",
    justifyContent: "center",
  },
  flowStep: {
    background: "#9b7b54",
    border: "1.5px solid #e0cdb0",
    borderRadius: "50px",
    padding: "0.4rem 1rem",
    fontSize: "0.88rem",
    fontWeight: 700,
    color: "#ffffff",
  },
  arrow: { color: "#c4a882", fontSize: "1rem", fontWeight: 700 },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "1.6rem",
    color: "#3d2b1a",
    marginBottom: "1.5rem",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: "1.25rem",
    marginBottom: "3.5rem",
  },
  featureCard: {
    textDecoration: "none",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
  },
  featureIcon: { fontSize: "2rem", marginBottom: "0.25rem" },
  featureTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "1.05rem",
    color: "#3d2b1a",
  },
  featureDesc: { color: "#9b7b54", fontSize: "0.87rem", lineHeight: 1.5 },
  featureArrow: { color: "#c4a882", fontSize: "0.85rem", fontWeight: 700, marginTop: "0.5rem" },
  ctaStrip: {
    background: "linear-gradient(135deg, #f0e8d8, #fae4cc)",
    borderRadius: "20px",
    padding: "2.5rem",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.75rem",
  },
  ctaTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: "1.6rem",
    color: "#3d2b1a",
  },
  ctaDesc: { color: "#9b7b54", marginBottom: "0.75rem" },
};
